import { useState, useCallback, useEffect, RefObject } from "react";
import { useTheme } from "@mui/material";
import { videoSources, VideoBreakpoint } from "./HeroSection.config";

interface UseResponsiveVideoProps {
  videoRef?: RefObject<HTMLVideoElement | null>;
}

interface UseResponsiveVideoResult {
  videoSrc: string | null;
  isVideoPlaying: boolean;
  currentBreakpoint: VideoBreakpoint;
  handleVideoPlaying: () => void;
}

export const useResponsiveVideo = (
  props?: UseResponsiveVideoProps
): UseResponsiveVideoResult => {
  const videoRef = props?.videoRef;
  const theme = useTheme();
  const breakpoints = theme.breakpoints.values;

  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [currentBreakpoint, setCurrentBreakpoint] = useState<VideoBreakpoint>(
    VideoBreakpoint.Desktop
  );

  const getBreakpointForWidth = useCallback(
    (width: number): VideoBreakpoint => {
      if (width < breakpoints.sm) return VideoBreakpoint.Mobile;
      if (width < breakpoints.xl) return VideoBreakpoint.Tablet;
      return VideoBreakpoint.Desktop;
    },
    [breakpoints.sm, breakpoints.xl]
  );

  const getVideoSourceForBreakpoint = useCallback(
    (bp: VideoBreakpoint): string => videoSources[bp],
    []
  );

  // Set video source on mount and update on resize (with debounce)
  useEffect(() => {
    const updateVideoSource = () => {
      const newBreakpoint = getBreakpointForWidth(window.innerWidth);
      const newSrc = getVideoSourceForBreakpoint(newBreakpoint);

      setVideoSrc((prev) => {
        if (prev !== newSrc) {
          // Reset playing state when source changes - poster will show again
          setIsVideoPlaying(false);
          setCurrentBreakpoint(newBreakpoint);
          return newSrc;
        }
        return prev;
      });

      // Update breakpoint even if source didn't change (for poster display)
      setCurrentBreakpoint(newBreakpoint);
    };

    // Set initial value
    updateVideoSource();

    // Debounced resize handler
    let timeoutId: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateVideoSource, 150);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, [getBreakpointForWidth, getVideoSourceForBreakpoint]);

  const handleVideoPlaying = useCallback(() => {
    setIsVideoPlaying(true);
  }, []);

  // Fallback: check if video is already playing on mount (cached)
  // Only hide poster if video is actually playing (not paused by low power mode)
  useEffect(() => {
    const video = videoRef?.current;
    if (video && !video.paused && video.currentTime > 0) {
      setIsVideoPlaying(true);
    }
  }, [videoSrc, videoRef]);

  return {
    videoSrc,
    isVideoPlaying,
    currentBreakpoint,
    handleVideoPlaying,
  };
};

